import { PropsOf } from "@emotion/react";
import styled from "@emotion/styled";
import { Drawer } from "antd";
import { useIsMobile } from "hooks/useDeviceType";
import { ActivityStore } from "store/activity/ActivityStore";
import { GlobalStore } from "store/global/GlobalStore";
import { Token } from "store/global/TokenEnum";
import { TransactionDetail } from "types/history";
import { ActivityDetailDescription } from "./elements/ActivityDetailDescription";
import { ActivityDetailTradeLegs } from "./elements/ActivityDetailTradeLegs";
import { CloseButton } from "./elements/CloseButton";
import { ShareTradeBtn } from "./elements/ShareTradeBtn";
import { ViewTxBtn } from "./elements/ViewTxBtn";

interface ActivityDetailedViewProps extends PropsOf<typeof Drawer> {
  txHash: TransactionDetail["txHash"];
  closeView: () => void;
}

export const ActivityDetailedView = ({
  txHash,
  closeView,
  ...drawerProps
}: ActivityDetailedViewProps) => {
  const theme = GlobalStore.useState(s => s.theme);
  const history = ActivityStore.useState(s => s.history);
  const isMobile = useIsMobile();

  if (history.error != null || history.data === undefined) {
    return null;
  }

  const txDetails = history.data.results[txHash];

  return (
    <Drawer
      {...drawerProps}
      style={{
        backgroundColor: theme.surface.main.primary.default,
        borderRadius: "25px 0px 0px 25px",
        // Mobile bottom navbar is 68px, but drawer component extends to 100dvh by default
        height: isMobile ? "calc(100dvh - 68px)" : "100%",
      }}
      // Remove default header that comes with drawer from antd
      headerStyle={{ display: "none" }}
      bodyStyle={{ paddingTop: "16px" }}
    >
      <CloseButtonContainer>
        <CloseButton closeView={closeView} />
      </CloseButtonContainer>
      <ActivityDetailTradeLegs
        sellTokens={txDetails.sellTokens}
        buyTokens={txDetails.buyTokens}
        isTradeAndSend={txDetails.sender !== txDetails.receiver}
      />
      <ActivityDetailDescription
        chainId={txDetails.chain_id}
        fee={txDetails.gasFeeUsd}
        txHash={txHash}
        sender={txDetails.sender}
        receiver={txDetails.receiver}
      />

      {/* CTA Buttons */}
      <CtaBtnContainer>
        <ViewTxBtn chainId={txDetails.chain_id} txHash={txHash} />
        <ShareTradeBtn
          sellTokenSymbols={Object.keys(txDetails.sellTokens) as Array<Token>}
          buyTokenSymbols={Object.keys(txDetails.buyTokens) as Array<Token>}
        />
      </CtaBtnContainer>
    </Drawer>
  );
};

// Sub-components

const CloseButtonContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: end;
  margin-bottom: 32px;
`;

const CtaBtnContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
